import type { Event, TodayEvent, HomeData, CategoryOption } from '@/types';

const configuredApiBase = import.meta.env.VITE_API_BASE?.trim() ?? '';
const apiBase = configuredApiBase.replace(/\/+$/, '');

export function getApiUrl(path: `/${string}`): string {
  return `${apiBase}${path}`;
}

function getAbsoluteApiUrl(path: `/${string}`): string {
  return new URL(getApiUrl(path), window.location.origin).toString();
}

export async function fetchHomeData(signal?: AbortSignal): Promise<HomeData> {
  const response = await fetch(getApiUrl('/api/home'), { signal });
  if (!response.ok) {
    throw new Error('Failed to fetch home data');
  }
  return response.json();
}

export async function fetchTodayEvent(signal?: AbortSignal): Promise<TodayEvent> {
  const response = await fetch(getApiUrl('/api/today'), { signal });
  if (!response.ok) {
    throw new Error('Failed to fetch today event');
  }
  return response.json();
}

export async function fetchUpcomingEvents(
  limit: number = 50,
  signal?: AbortSignal,
): Promise<{ events: Event[]; is_today: boolean }> {
  const response = await fetch(getApiUrl(`/api/upcoming?limit=${limit}`), { signal });
  if (!response.ok) {
    throw new Error('Failed to fetch upcoming events');
  }
  return response.json();
}

export async function fetchPastEvents(limit: number = 10, signal?: AbortSignal): Promise<Event[]> {
  const response = await fetch(getApiUrl(`/api/past?limit=${limit}`), { signal });
  if (!response.ok) {
    throw new Error('Failed to fetch past events');
  }
  return response.json();
}

export async function fetchRecentChanges(
  days: number = 30,
  minLimit: number = 10,
  signal?: AbortSignal,
): Promise<Event[]> {
  const response = await fetch(
    getApiUrl(`/api/changelog?days=${days}&min_limit=${minLimit}`),
    { signal },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch changelog');
  }
  return response.json();
}

export function getCalendarUrl(): string {
  return getAbsoluteApiUrl('/aviation.ics');
}

export function getCalendarSubscribeUrl(): string {
  return getCalendarUrl().replace(/^https?:\/\//, 'webcal://');
}

export async function fetchTags(signal?: AbortSignal): Promise<string[]> {
  const response = await fetch(getApiUrl('/api/tags'), { signal });
  if (!response.ok) {
    throw new Error('Failed to fetch tags');
  }
  return response.json();
}

export async function fetchCategories(signal?: AbortSignal): Promise<string[]> {
  const response = await fetch(getApiUrl('/api/categories'), { signal });
  if (!response.ok) {
    throw new Error('Failed to fetch categories');
  }
  return response.json();
}

export async function fetchCategoryOptions(signal?: AbortSignal): Promise<CategoryOption[]> {
  const response = await fetch(getApiUrl('/api/category-options'), { signal });
  if (!response.ok) {
    throw new Error('Failed to fetch category options');
  }
  return response.json();
}

export function getCalendarUrlWithTags(tags: string[]): string {
  const url = new URL(getCalendarUrl());
  for (const tag of tags) {
    url.searchParams.append('tag', tag);
  }
  return url.toString();
}

export function getCalendarSubscribeUrlWithTags(tags: string[]): string {
  return getCalendarUrlWithTags(tags).replace(/^https?:\/\//, 'webcal://');
}

export function getCalendarUrlWithCategory(category: string | null): string {
  const url = new URL(getCalendarUrl());
  if (category) {
    url.searchParams.set('category', category);
  }
  return url.toString();
}

export function getCalendarSubscribeUrlWithCategory(category: string | null): string {
  return getCalendarUrlWithCategory(category).replace(/^https?:\/\//, 'webcal://');
}

export async function fetchAllEvents(
  category?: string | null,
  signal?: AbortSignal,
): Promise<Event[]> {
  let path: `/${string}` = '/api/events';
  if (category) {
    path = `/api/events?category=${encodeURIComponent(category)}`;
  }
  const response = await fetch(getApiUrl(path), { signal });
  if (!response.ok) {
    throw new Error('Failed to fetch events');
  }
  return response.json();
}
